/**
 * The main thread's handle on the device's local database.
 *
 * Calls look asynchronous because they cross into a worker, which is exactly
 * how the UI already talks to the server. Swapping one for the other is
 * therefore a change of transport, not a change of shape — which is why the
 * screens did not have to be rewritten to gain an offline mode.
 */

import type { AuthenticatedUser } from '@bakery/shared';

import type { BootResult, WorkerRequest, WorkerResponse } from './protocol.js';

type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void };

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

let bootState: BootResult = {
  ready: false,
  reason: 'not started',
  deviceCode: null,
  deviceId: null,
  driver: null,
};

function ensureWorker(): Worker {
  if (worker) return worker;

  worker = new Worker(new URL('./db.worker.ts', import.meta.url), { type: 'module' });

  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const { id, ok, result, error } = event.data;
    const waiting = pending.get(id);
    if (!waiting) return;
    pending.delete(id);

    if (ok) {
      waiting.resolve(result);
    } else {
      const failure = new Error(error?.message ?? 'Local database error');
      failure.name = error?.name ?? 'Error';
      waiting.reject(failure);
    }
  };

  worker.onerror = (event) => {
    // The worker died. Fail everything waiting rather than leaving the UI hung.
    const failure = new Error(event.message || 'The local database worker stopped');
    for (const [, waiting] of pending) waiting.reject(failure);
    pending.clear();
    bootState = { ready: false, reason: failure.message, deviceCode: null, deviceId: null, driver: null };
    worker = null;
  };

  return worker;
}

function send<T>(request: Omit<WorkerRequest, 'id'>): Promise<T> {
  const active = ensureWorker();
  const id = nextId++;

  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
    active.postMessage({ ...request, id } satisfies WorkerRequest);
  });
}

/**
 * Start the local database and report whether this device can hold one.
 *
 * Never throws. A device without OPFS — an old Android WebView, a browser with
 * site data blocked — is a device that works online only, and the app has to be
 * able to say so plainly rather than appearing broken.
 */
let booting: Promise<BootResult> | null = null;

export async function startLocalDb(): Promise<BootResult> {
  if (booting) return booting;
  booting = doStart();
  return booting;
}

/**
 * The boot, awaited rather than polled.
 *
 * Callers need this because the database takes a second or two to come up while
 * the screens start querying immediately. Checking a "ready" flag meant the
 * first request after a reload found it false, gave up, and reported the shop
 * offline — with a perfectly good local database finishing its boot moments
 * later. Awaiting removes the race instead of narrowing it.
 */
export function whenLocalDbReady(): Promise<BootResult> {
  return booting ?? startLocalDb();
}

async function doStart(): Promise<BootResult> {
  try {
    bootState = await send<BootResult>({ kind: 'boot' });
  } catch (error) {
    bootState = {
      ready: false,
      reason: (error as Error).message,
      deviceCode: null,
      deviceId: null,
      driver: null,
    };
  }
  return bootState;
}

export function localDbState(): BootResult {
  return bootState;
}

export function localDbReady(): boolean {
  return bootState.ready;
}

/**
 * Run an unauthenticated command — signing in, and remembering a credential.
 *
 * Separate from `localCommand` because it carries no user: it is what produces
 * one when there is no server to ask.
 */
export function localAuth<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  return send<T>({ kind: 'auth', name, args });
}

/** Run a command against the local database. Throws if this device has none. */
export function localCommand<T>(
  name: string,
  args: Record<string, unknown>,
  user: AuthenticatedUser,
): Promise<T> {
  return send<T>({ kind: 'command', name, args, user });
}
